/*
defines functionality for dragging and dropping files into the graph
*/
import {compressImage} from '../utilities/utilities';


/**
 * represents a controller for drag-and-drop files
 */
export default class FileController {
    /**
     * makes a new file controller given a graph controller, model, and view
     * @param {GraphController} graphController the graph controller to use
     * @param {Graph} model the graph model instance to use
     * @param {GraphView} view the graph view to use
     */
    constructor(graphController, model, view) {
        this.graphController = graphController;
        this.model = model;
        this.view = view;
    }

    initHandlers() {
        this.initFileUploadHandler();
    }

    /**
     * initializes the handler for dragging-and-dropping files
     */
    initFileUploadHandler() {
        this.view.svg
            .on('dragover', () => this.onFileDragOver())
            .on('dragleave', () => this.onFileDragLeave())
            .on('drop', () => this.onFileDrop());
    }

    /**
     * when files are dragged into the window, show feedback
     */
    onFileDragOver() {
        let event = d3.event;

        // prevent the browser from redirecting / random default behavior
        event.stopPropagation();
        event.preventDefault();

        // show the little plus sign
        event.dataTransfer.dropEffect = 'copy';

        // show feedback
        this.graphController.state.set('DROPPING');
    }

    onFileDragLeave() {
        this.graphController.state.set('NONE');
    }

    /**
     * when files are dropped into the window, copy them and discard them
     */
    onFileDrop() {
        let event = d3.event;

        // prevent the browser from redirecting / random default behavior
        event.stopPropagation();
        event.preventDefault();

        this.readFiles(event);

        this.graphController.state.set('NONE');
        this.removeDragData(event);
    }

    /**
     * remove already-imported dragged files
     * @param {Event} event the event object which contains the files
     */
    removeDragData(event) {
        if (event.dataTransfer.items) {
            // Use DataTransferItemList interface to remove the drag data
            event.dataTransfer.items.clear();
        } else {
            // Use DataTransfer interface to remove the drag data
            event.dataTransfer.clearData();
        }
    }

    /**
     * read and import the files currently being dragged in
     * @param {Event} event the event object which contains the files to read
     */
    readFiles(event) {
        if (event.dataTransfer.items) {
            for (let i = 0; i < event.dataTransfer.items.length; i++) {
                // If dropped items aren't files, reject them
                if (event.dataTransfer.items[i].kind === 'file') {
                    let file = event.dataTransfer.items[i].getAsFile();
                    if (file.type === 'image/jpeg' || file.type === 'image/png') {
                        this.readImageFile(file);
                    }
                }
            }
        } else {
            for (let i = 0; i < event.dataTransfer.files.length; i++) {
                if (file.type === 'image/jpeg' || file.type === 'image/png') {
                    this.readImageFile(file);
                }
            }
        }
    }

    /**
     * read and import the given image file
     * @param {File} file the image file to import
     */
    readImageFile(file) {
        let fr = new FileReader;
        fr.onload = () => {
            let img = new Image;

            img.onload = () => {
                let compressedImage = compressImage(img, 250, 1)
                compressedImage.onload = () => {
                    let mouseX, mouseY;

                    try {
                        [mouseX, mouseY] = d3.mouse(this.view.svg.node());
                    } catch(e) {
                        mouseX = this.view.width / 2,
                        mouseY = this.view.height / 2;
                    }

                    this.graphController.addLocalImageNode(
                        {
                            title: file.name,
                            width: compressedImage.width,
                            height: compressedImage.height,
                            url: compressedImage.src,
                        },
                        mouseX,
                        mouseY
                    );
                }
            };

            img.src = fr.result;
        };

        fr.readAsDataURL(file);
    }
}